/**
 * Event creation wizard shell (`/me/events/new/*`).
 *
 * Route based rather than state based: every step is linkable and refreshable,
 * and the answers live in `useEventDraft` so the steps can be visited in
 * any order. The shell owns the header and the step indicator; each step owns
 * one form.
 */

import { Outlet, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

import { PageHeader } from '../../../components/layout/page-header'
import { WizardSteps, type WizardStep } from '../../../components/layout/wizard'
import { useEventDraft } from '../../../stores/drafts'

/** Maps the current pathname onto the step key `WizardSteps` highlights. */
function currentStepKey(pathname: string): string {
  if (pathname.endsWith('/activities')) {
    return 'activities'
  }
  if (pathname.endsWith('/weather')) {
    return 'weather'
  }
  if (pathname.endsWith('/review')) {
    return 'review'
  }
  return 'title'
}

export function NewEventLayout() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { draft } = useEventDraft()

  const steps: WizardStep[] = [
    { key: 'title', label: t('eventNew.stepTitle'), to: '/me/events/new/title' },
    { key: 'activities', label: t('eventNew.stepActivities'), to: '/me/events/new/activities' },
    { key: 'weather', label: t('eventNew.stepWeather'), to: '/me/events/new/weather' },
    { key: 'review', label: t('eventNew.stepReview'), to: '/me/events/new/review' },
  ]

  return (
    <div className="container-narrow flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: t('nav.me'), to: '/me' },
          { label: t('nav.events'), to: '/me/events' },
          { label: t('eventNew.title') },
        ]}
        title={t('eventNew.title')}
        description={draft.title.length > 0 ? draft.title : undefined}
      />
      <WizardSteps steps={steps} current={currentStepKey(pathname)} />
      <Outlet />
    </div>
  )
}
